import React from 'react';
import {
  Avatar,
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { getFullName, getInitials } from '@app/utils/user';
import { createStyles } from './ParticipantsPreview.styles';
import type {
  ParticipantsPreviewParticipant,
  ParticipantsPreviewProps,
} from './ParticipantsPreview.types';

export const MAX_VISIBLE_PARTICIPANTS = 4;
export const MAX_DISPLAYED_REMAINING_PARTICIPANTS = 99;

const getParticipantName = (participant: ParticipantsPreviewParticipant) =>
  String(getFullName(participant) || '').trim() || 'Unknown participant';

const ParticipantsPreview = ({
  participantTotal,
  participants,
}: ParticipantsPreviewProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const showDesktopTooltips = useMediaQuery(theme.breakpoints.up('md'));
  const visibleParticipants = participants.slice(0, MAX_VISIBLE_PARTICIPANTS);
  const remainingCount = Math.max(
    participantTotal - visibleParticipants.length,
    0
  );
  const displayedRemainingCount = Math.min(
    remainingCount,
    MAX_DISPLAYED_REMAINING_PARTICIPANTS
  );

  return (
    <Box
      aria-label="Participants preview"
      data-testid="participants-preview"
      sx={styles.preview}
    >
      <Typography component="span" sx={styles.label}>
        Participants
      </Typography>

      {(visibleParticipants.length > 0 ||
        participantTotal > MAX_VISIBLE_PARTICIPANTS) && (
        <Box sx={styles.participantSummary}>
          <Box aria-label="Participant avatars" sx={styles.avatarStack}>
            {visibleParticipants.map((participant, index) => {
              const fullName = getParticipantName(participant);
              const participantKey =
                participant.id ||
                participant.objectId ||
                `${fullName}-${index}`;
              const avatar = (
                <Box
                  component="span"
                  data-testid="participant-preview-avatar"
                  sx={styles.avatarWrapper(index, visibleParticipants.length)}
                  tabIndex={showDesktopTooltips ? 0 : undefined}
                >
                  <Avatar
                    altText={fullName}
                    fallbackText={getInitials(participant)}
                    kind="circular"
                    src={participant.avatar || undefined}
                    sx={{
                      ...styles.avatar,
                      ...(participant.avatar ? styles.imageAvatar : {}),
                    }}
                  />
                </Box>
              );

              return showDesktopTooltips ? (
                <Tooltip key={participantKey} position="top" title={fullName}>
                  {avatar}
                </Tooltip>
              ) : (
                <React.Fragment key={participantKey}>{avatar}</React.Fragment>
              );
            })}

            {participantTotal > MAX_VISIBLE_PARTICIPANTS && (
              <Box
                aria-label={`${remainingCount} additional participants`}
                component="span"
                data-testid="participants-preview-remaining"
                role="img"
                sx={styles.avatarWrapper(
                  visibleParticipants.length,
                  visibleParticipants.length
                )}
              >
                <Avatar
                  aria-hidden="true"
                  fallbackText={`+${displayedRemainingCount}`}
                  kind="circular"
                  sx={styles.avatar}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ParticipantsPreview;
